import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HeatmapParams } from '@core/models/heatmap-params.model';
import { ImageHeatmapConfig } from '@core/models/image-heatmap-config';
import { MlResults } from '@core/models/ml-results.model';
import { environment } from '@environment/environment';
import { BehaviorSubject, Observable, catchError, throwError } from 'rxjs';
import { AuthService } from '../auth-service/auth-service.service';
import { InferenceStatus } from './inference-status.model';

export interface HeatmapData {
  width: number;
  height: number;
  values: number[];
}

export interface HeatmapResponse {
  image_id: number;
  heatmap: HeatmapData;
}

@Injectable({
  providedIn: 'root',
})
export class InferenceService {
  heatmapConfigs = new Map<number, ImageHeatmapConfig>();
  updateHeatmap = new BehaviorSubject<number>(-1);

  serializeImageHeatmapConfig(ihc: ImageHeatmapConfig) {
    return JSON.stringify({
      modelNameActive: ihc.modelNameActive,
      configs: [...ihc.configs],
      timestamp: ihc.timestamp,
    });
  }

  serializeConfigMap(config: Map<number, ImageHeatmapConfig>) {
    return JSON.stringify(
      [...config].map((x) => [x[0], this.serializeImageHeatmapConfig(x[1])]),
    );
  }

  desserializeImageHeatmapConfig(myStr: string) {
    const data = JSON.parse(myStr);
    const ihc = new ImageHeatmapConfig();
    ihc.modelNameActive = data.modelNameActive;
    ihc.configs = new Map(data.configs);
    ihc.timestamp = data.timestamp || Date.now();
    return ihc;
  }

  desserializeConfigMap(myStr: string) {
    let data = JSON.parse(myStr);
    data = data.map((x: any[]) => [
      x[0],
      this.desserializeImageHeatmapConfig(x[1]),
    ]);
    return new Map(data);
  }

  loadConfiguration() {
    const myEmail = this.authService.getLoggedUser()?.email;
    try {
      const data = localStorage.getItem(`${myEmail}_heatmapPreferences`);
      if (data === null) return;
      this.heatmapConfigs = this.desserializeConfigMap(data) as Map<
        number,
        ImageHeatmapConfig
      >;
    } catch (err) {
      console.error(err);
    }
  }

  saveConfiguration() {
    const myEmail = this.authService.getLoggedUser()?.email;

    if (this.heatmapConfigs.size > 100) {
      const configs = Array.from(this.heatmapConfigs.entries());
      configs.sort((a, b) => a[1].timestamp - b[1].timestamp);

      while (configs.length > 100) {
        const [imageId] = configs.shift()!;
        this.heatmapConfigs.delete(imageId);
      }
    }

    const serializedData = this.serializeConfigMap(this.heatmapConfigs);
    localStorage.setItem(`${myEmail}_heatmapPreferences`, serializedData);
  }

  constructor(
    private httpClient: HttpClient,
    private authService: AuthService,
  ) {
    this.loadConfiguration();
  }

  containsHeatmapConfig(image_id: number, model_name: string): boolean {
    if (!this.heatmapConfigs.has(image_id)) return false;
    const contains = this.heatmapConfigs.get(image_id)?.configs.has(model_name);
    return contains === undefined ? false : contains;
  }

  getActiveModel(image_id: number): string {
    if (!this.heatmapConfigs.has(image_id)) {
      return '';
    }
    const config = this.heatmapConfigs.get(image_id);
    return config?.modelNameActive || '';
  }

  getActiveHeatmapConfig(image_id: number): HeatmapParams {
    if (!this.heatmapConfigs.has(image_id)) {
      return new HeatmapParams();
    }

    const modelActive = this.heatmapConfigs.get(image_id)
      ?.modelNameActive as string;
    if (!this.heatmapConfigs.get(image_id)?.configs.has(modelActive)) {
      return new HeatmapParams();
    }
    return (
      this.heatmapConfigs.get(image_id)?.configs.get(modelActive) ??
      new HeatmapParams()
    );
  }

  setActiveConfig(image_id: number, model_name: string, params: HeatmapParams) {
    if (!this.heatmapConfigs.has(image_id)) {
      const config = new ImageHeatmapConfig();
      const defaultParams = new HeatmapParams();
      defaultParams.active = true;
      defaultParams.color = params.color || defaultParams.color;
      defaultParams.opacity = params.opacity || defaultParams.opacity;
      defaultParams.minPercentile =
        params.minPercentile || defaultParams.minPercentile;
      defaultParams.maxPercentile =
        params.maxPercentile || defaultParams.maxPercentile;
      defaultParams.needsRefresh = true;
      config.configs.set(model_name, defaultParams);
      config.modelNameActive = model_name;
      this.heatmapConfigs.set(image_id, config);
    }
    const configToAdd = this.heatmapConfigs.get(image_id);
    if (configToAdd) {
      if (params.active) {
        configToAdd.modelNameActive = model_name;
      }
      if (!configToAdd.configs.has(model_name)) {
        const defaultParams = new HeatmapParams();
        defaultParams.active = true;
        defaultParams.color = params.color || defaultParams.color;
        defaultParams.opacity = params.opacity || defaultParams.opacity;
        defaultParams.minPercentile =
          params.minPercentile || defaultParams.minPercentile;
        defaultParams.maxPercentile =
          params.maxPercentile || defaultParams.maxPercentile;
        defaultParams.needsRefresh = true;
        configToAdd.configs.set(model_name, defaultParams);
      }
      const currentParams = configToAdd.configs.get(model_name);
      if (currentParams) {
        if (params.active !== undefined) {
          currentParams.active = params.active;
        }
        if (params.color) currentParams.color = params.color;
        if (params.opacity !== undefined)
          currentParams.opacity = params.opacity;
        if (params.minPercentile !== undefined)
          currentParams.minPercentile = params.minPercentile;
        if (params.maxPercentile !== undefined)
          currentParams.maxPercentile = params.maxPercentile;
      }
      configToAdd.timestamp = Date.now();
      if (params.active) {
        configToAdd.configs.forEach((value, key) => {
          if (key !== model_name) {
            value.active = false;
          }
        });
      }
    }
    this.saveConfiguration();
  }

  removeConfig(image_id: number, model_name: string) {
    if (!this.heatmapConfigs.has(image_id)) {
      return;
    }
    this.heatmapConfigs.get(image_id)?.configs.delete(model_name);
    if (this.heatmapConfigs.get(image_id)?.configs.size === 0) {
      this.heatmapConfigs.delete(image_id);
    }
    this.saveConfiguration();
  }

  getConfig(image_id: number, model_name: string) {
    if (this.containsHeatmapConfig(image_id, model_name)) {
      return this.heatmapConfigs
        .get(image_id)
        ?.configs.get(model_name) as HeatmapParams;
    }
    return new HeatmapParams();
  }

  runInference(model_name: string, image_id: number): Observable<any> {
    const serviceUrl = `${environment.examInfoBackend}/slide/inference/${model_name}/${image_id}`;
    return this.httpClient
      .post(serviceUrl, {})
      .pipe(catchError((error) => throwError(() => error)));
  }

  controlHeatmap(image_id: number, activated: boolean) {
    const urlPart = activated ? 'activate' : 'deactivate';
    const serviceUrl = `${environment.examInfoBackend}/slide/heatmap/${image_id}/${urlPart}`;
    return this.httpClient.post(serviceUrl, {});
  }

  executionStatus(image_id: number): Observable<InferenceStatus> {
    const serviceUrl = `${environment.examInfoBackend}/slide/inference_status/${image_id}`;
    return this.httpClient.get<InferenceStatus>(serviceUrl);
  }

  getAvailableModels(): Observable<{ models: any }> {
    const route = `${environment.examInfoBackend}/slide/inference/models`;
    return this.httpClient.get<{ models: any }>(route);
  }

  inferenceStatus(image_id: number): Observable<MlResults[]> {
    const serviceUrl = `${environment.examInfoBackend}/slide/inference/results/${image_id}`;
    return this.httpClient.get<MlResults[]>(serviceUrl);
  }

  deleteInference(result_id: number): Observable<MlResults[]> {
    const route = `${environment.examInfoBackend}/slide/inference/results/delete/${result_id}`;
    return this.httpClient.post<MlResults[]>(route, {});
  }

  getHeatmapData(
    model_name: string,
    image_id: number,
  ): Observable<HeatmapResponse> {
    const serviceUrl = `${environment.examInfoBackend}/slide/heatmap_raw/${model_name}/${image_id}`;
    return this.httpClient
      .get<HeatmapResponse>(serviceUrl)
      .pipe(catchError((error) => throwError(() => error)));
  }
}
